import type { ReactElement } from "react";
import React from "react";

import { StarFilledIcon } from "~/components/ui/icons/star-filled";
import { StarHalfFilledIcon } from "~/components/ui/icons/star-half-filled";

export type RatingProps = {
  /**
   * Number of stars. Can be a float number.
   */
  value: number;
} & React.ComponentPropsWithoutRef<"div">;

/**
 * Main Rating component for user interaction.
 */
export const Rating: React.FC<RatingProps> = ({ value }) => {
  const arrayOfItems = Array<ReactElement>(5);

  arrayOfItems.fill(
    <StarFilledIcon
      className="size-4 text-green-300"
      data-testid="rating-empty"
    />,
    0,
    5,
  );

  const numberOfFilledItems = value > 5 ? 5 : Math.trunc(value);

  for (let i = 0; i < numberOfFilledItems; i++) {
    arrayOfItems[i] = (
      <StarFilledIcon
        className="size-4 text-green-900"
        data-testid="rating-full"
      />
    );
  }

  const decimalPartOfRatingValue = value % 1;
  if (decimalPartOfRatingValue >= 0.75) {
    arrayOfItems[numberOfFilledItems] = (
      <StarFilledIcon
        className="size-4 text-green-900"
        data-testid="rating-full"
      />
    );
  } else if (decimalPartOfRatingValue >= 0.25) {
    arrayOfItems[numberOfFilledItems] = (
      <StarHalfFilledIcon
        className="size-4 text-green-900"
        data-testid="rating-half"
      />
    );
  }

  return (
    <div className="flex space-x-1">
      {arrayOfItems.map((item, index) =>
        React.cloneElement(item, { key: index }),
      )}
    </div>
  );
};

Rating.displayName = "Rating";
